import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, GitBranch, Info, Layers, RotateCcw } from 'lucide-react'
import { CodeBlocksIcon } from '@/components/icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  ClaudeCodeNodeDescriptor,
  ClaudeEffort,
  ClaudeModel,
  ClaudePerNodeOverride,
  ClaudeRunOverrides,
} from '@/types/claude'
import type { ConnectorType } from '@/types'
import { RepoPromptPicker } from './shared/RepoPromptPicker'
import { REPO_CONNECTOR_TYPES } from './shared/repoConnectorTypes'
import { useAgentModelCatalog } from '@/hooks'

interface Props {
  baselineModel: string
  baselineEffort: string
  claudeCodeNodes: ClaudeCodeNodeDescriptor[]
  projectId: string
  connectorTypes: ConnectorType[]
  value: ClaudeRunOverrides
  onChange: (next: ClaudeRunOverrides) => void
}

const FALLBACK_EFFORT = 'medium'

function clampEffort(effort: string | undefined, modelEfforts: ClaudeEffort[]): ClaudeEffort {
  return effort && modelEfforts.includes(effort) ? effort : modelEfforts[0] ?? FALLBACK_EFFORT
}

const FIELD_LABEL_CLASS = 'text-[10px] font-bold uppercase tracking-wider text-foreground'

export function ClaudeSettingsSection({
  baselineModel,
  baselineEffort,
  claudeCodeNodes,
  projectId,
  connectorTypes,
  value,
  onChange,
}: Props) {
  const [perNodeOpen, setPerNodeOpen] = useState(() => claudeCodeNodes.some(n => n.promptDeferred))
  const catalog = useAgentModelCatalog('claude-code')

  const clampModel = (slug?: string) =>
    slug && catalog.models.some(m => m.id === slug) ? slug : (catalog.defaultModel ?? catalog.models[0]?.id ?? '')

  const safeBaselineModel = clampModel(baselineModel)
  const safeBaselineEffort = clampEffort(baselineEffort, catalog.effortsFor(safeBaselineModel))

  const runModel: ClaudeModel = value.model ?? safeBaselineModel
  const runEffort: ClaudeEffort = clampEffort(value.effort ?? safeBaselineEffort, catalog.effortsFor(runModel))
  const hasAnyOverride =
    !!(value.model || value.effort) ||
    !!(value.perNode && Object.keys(value.perNode).length > 0)
  const isRunWideSet = !!(value.model || value.effort)

  const nodesWithYamlDefaults = claudeCodeNodes.filter(n => n.yamlModel || n.yamlEffort)

  const effectiveBaseFor = (nodeName: string): { model: ClaudeModel; effort: ClaudeEffort } => {
    const node = claudeCodeNodes.find(n => n.name === nodeName)
    const model = value.model ?? node?.yamlModel ?? safeBaselineModel
    const effort = clampEffort(
      value.effort ?? node?.yamlEffort ?? safeBaselineEffort,
      catalog.effortsFor(model),
    )
    return { model, effort }
  }

  const resolvedFor = (nodeName: string): { model: ClaudeModel; effort: ClaudeEffort } => {
    const o = value.perNode?.[nodeName]
    const base = effectiveBaseFor(nodeName)
    const model = o?.model ?? base.model
    const effort = clampEffort(o?.effort ?? base.effort, catalog.effortsFor(model))
    return { model, effort }
  }

  const isPerNodeOverridden = (nodeName: string): boolean => {
    const o = value.perNode?.[nodeName]
    if (!o) return false
    if (o.repoPrompt) return true
    const base = effectiveBaseFor(nodeName)
    return (!!o.model && o.model !== base.model) || (!!o.effort && o.effort !== base.effort)
  }

  const overrideCount = claudeCodeNodes.filter(n => isPerNodeOverridden(n.name)).length

  const handleRunModelChange = (next: ClaudeModel) => {
    const supported = catalog.effortsFor(next)
    const nextEffort = supported.includes(runEffort) ? runEffort : supported[0]
    onChange({ ...value, model: next, effort: nextEffort })
  }

  const handleRunEffortChange = (next: ClaudeEffort) => {
    onChange({ ...value, effort: next })
  }

  const handleResetAll = () => {
    onChange({ model: undefined, effort: undefined, perNode: undefined })
  }

  const defaultRepoPrompt = useMemo(
    () => ({ connectorType: connectorTypes.find(t => REPO_CONNECTOR_TYPES.includes(t)) ?? '', repository: '', filePath: '' }),
    [connectorTypes],
  )

  if (catalog.isLoading) return null

  if (catalog.models.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" role="alert" data-testid="claude-settings-unavailable">
        Claude model options could not be loaded. Reload the page to try again.
      </p>
    )
  }

  const setPerNode = (name: string, patch: ClaudePerNodeOverride | null) => {
    const next = { ...(value.perNode ?? {}) }
    if (patch === null) delete next[name]
    else next[name] = patch
    onChange({ ...value, perNode: Object.keys(next).length ? next : undefined })
  }

  // Merge a partial change into a node's override, preserving the other fields (model/effort/repoPrompt).
  const patchPerNode = (name: string, patch: Partial<ClaudePerNodeOverride>) => {
    const merged = { ...(value.perNode?.[name] ?? {}), ...patch }
    const cleaned: ClaudePerNodeOverride = {}
    if (merged.model) cleaned.model = merged.model
    if (merged.effort) cleaned.effort = merged.effort
    if (merged.repoPrompt) cleaned.repoPrompt = merged.repoPrompt
    setPerNode(name, Object.keys(cleaned).length ? cleaned : null)
  }

  return (
    <section className="space-y-3" data-testid="claude-settings-section">
      <h3 className="text-sm font-semibold text-card-foreground">Claude Code settings</h3>
      
      <div className="rounded-md border border-border bg-card p-3 text-xs text-muted-foreground">
        <div className="flex flex-col items-start gap-2">
          <div className='flex items-center gap-2'>
            {catalog.modelLabel(safeBaselineModel)}
            <Badge>{catalog.effortLabel(safeBaselineEffort)} effort</Badge>
          </div>
          <p>
            Adjust below to override for this run, or expand &ldquo;Customize per node&rdquo; to override individual nodes.
          </p>
        </div>
      </div>

      {nodesWithYamlDefaults.length > 0 && (
        <div
          className="rounded-md border border-warning-foreground bg-warning p-3 text-xs text-warning-foreground"
          data-testid="claude-yaml-defaults-warning"
          role="status"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-foreground" aria-hidden />
            <div className="flex-1">
              <p className="font-bold">
                By default, {nodesWithYamlDefaults.length} node{nodesWithYamlDefaults.length === 1 ? '' : 's'}{' '}
                {nodesWithYamlDefaults.length === 1 ? 'has' : 'have'} an author-specified default
                {isRunWideSet ? (
                  <>
                    . Your run-wide selection below overrides this, except for nodes you've explicitly customized per-node.
                  </>
                ) : (
                  <>
                    {' '}— see the per-node section below.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-x-3 gap-y-1">
        <Label className={FIELD_LABEL_CLASS}>Model</Label>
        <Label className={FIELD_LABEL_CLASS}>Effort</Label>
        <span />

        <Select value={runModel} onValueChange={v => handleRunModelChange(v as ClaudeModel)}>
          <SelectTrigger className="cursor-pointer" data-testid="claude-run-model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {catalog.models.map(m => (
              <SelectItem key={m.id} value={m.id} className="cursor-pointer">{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={runEffort} onValueChange={v => handleRunEffortChange(v as ClaudeEffort)}>
          <SelectTrigger className="cursor-pointer" data-testid="claude-run-effort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {catalog.effortsFor(runModel).map(e => (
              <SelectItem key={e} value={e} className="cursor-pointer">{catalog.effortLabel(e)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="button"
          variant="outline"
          onClick={handleResetAll}
          className={`h-10 cursor-pointer border-warning-foreground text-warning-foreground hover:bg-warning ${hasAnyOverride ? '' : 'invisible'}`}
          tabIndex={hasAnyOverride ? 0 : -1}
          aria-hidden={!hasAnyOverride}
          data-testid="claude-run-reset"
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" aria-hidden /> Reset
        </Button>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setPerNodeOpen(o => !o)}
          className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-foreground hover:text-foreground"
          aria-expanded={perNodeOpen}
          data-testid="claude-per-node-toggle"
        >
          {perNodeOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Customize per node
          {overrideCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-auto bg-warning text-warning-foreground hover:bg-warning"
            >
              {overrideCount} overridden
            </Badge>
          )}
        </button>

        {perNodeOpen && (
          <div className="p-2">
            <div className="grid grid-cols-[1fr_140px_120px_30px] gap-2 px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground">
              <div>Node</div>
              <div>Model</div>
              <div>Effort</div>
              <div></div>
            </div>

            <div className="max-h-[320px] overflow-y-auto">
              {claudeCodeNodes.map(n => {
                const r = resolvedFor(n.name)
                const isOverridden = isPerNodeOverridden(n.name)
                const hasYaml = !!(n.yamlModel || n.yamlEffort)
                const isYamlSuperseded = hasYaml && (
                  (!!n.yamlModel && n.yamlModel !== r.model) ||
                  (!!n.yamlEffort && n.yamlEffort !== r.effort)
                )
                const repoPrompt = value.perNode?.[n.name]?.repoPrompt
                return (
                  <div
                    key={n.name}
                    className={`rounded ${isOverridden ? 'bg-warning' : ''}`}
                    data-testid={`claude-per-node-row-${n.name}`}
                  >
                    <div className="grid grid-cols-[1fr_140px_120px_30px] items-center gap-2 px-2 py-1.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 flex-wrap text-xs">
                        <span className="inline-flex items-center gap-1 text-button font-medium capitalize">
                          <Layers className="h-3 w-3 shrink-0" aria-hidden />
                          {n.phaseName}
                        </span>
                        <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="inline-flex items-center gap-1 text-foreground capitalize">
                          <GitBranch className="h-3 w-3 shrink-0" aria-hidden />
                          {n.branchName}
                        </span>
                        <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="inline-flex items-center gap-1 text-foreground font-semibold min-w-0">
                          <CodeBlocksIcon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{n.name}</span>
                        </span>
                        {isOverridden && (
                          <Badge
                            variant="secondary"
                            className="bg-warning text-[10px] text-warning-foreground hover:bg-warning"
                          >
                            overridden
                          </Badge>
                        )}
                      </div>
                      {hasYaml && (
                        <div className={`flex items-center gap-1 flex-wrap text-[10px] mt-0.5 ${isYamlSuperseded ? 'text-warning-foreground' : 'text-success-foreground'}`}>
                          <Info className="h-2.5 w-2.5 shrink-0" aria-hidden />
                          <span>
                            Author preferred{' '}
                            <strong>
                              {n.yamlModel && n.yamlEffort
                                ? `${catalog.modelLabel(n.yamlModel)} · ${catalog.effortLabel(n.yamlEffort)}`
                                : n.yamlModel
                                  ? `${catalog.modelLabel(n.yamlModel)}`
                                  : `${catalog.effortLabel(n.yamlEffort!)} effort`}
                            </strong>
                            {isYamlSuperseded && (
                              <>
                                {' '}(superseded — using{' '}
                                <strong>
                                  {catalog.modelLabel(r.model)} · {catalog.effortLabel(r.effort)}
                                </strong>
                                )
                              </>
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    <Select
                      value={r.model}
                      onValueChange={v => {
                        const newModel = v as ClaudeModel
                        const supported = catalog.effortsFor(newModel)
                        const newEffort = supported.includes(r.effort) ? r.effort : supported[0]
                        patchPerNode(n.name, { model: newModel, effort: newEffort })
                      }}
                    >
                      <SelectTrigger className="h-8 cursor-pointer"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {catalog.models.map(m => (
                          <SelectItem key={m.id} value={m.id} className="cursor-pointer">{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={r.effort}
                      onValueChange={v => {
                        const newEffort = v as ClaudeEffort
                        patchPerNode(n.name, { model: r.model, effort: newEffort })
                      }}
                    >
                      <SelectTrigger className="h-8 cursor-pointer"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {catalog.effortsFor(r.model).map(e => (
                          <SelectItem key={e} value={e} className="cursor-pointer">{catalog.effortLabel(e)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {isOverridden ? (
                      <button
                        type="button"
                        onClick={() => setPerNode(n.name, null)}
                        className="cursor-pointer rounded p-1 text-warning-foreground hover:bg-warning"
                        aria-label={`Reset ${n.name} override`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span />
                    )}
                    </div>

                    <div className="flex flex-col gap-2 px-2 pb-2">
                      <div className="flex items-center gap-2">
                        <span className={FIELD_LABEL_CLASS}>Prompt</span>
                        {n.promptDeferred ? (
                          <span
                            className="rounded bg-warning px-2 py-0.5 text-[11px] font-semibold text-warning-foreground"
                            data-testid={`claude-per-node-prompt-required-${n.name}`}
                            role="status"
                          >
                            Prompt required - provided by the user
                          </span>
                        ) : (
                          <Select
                            value={repoPrompt ? 'repo' : 'system'}
                            onValueChange={v =>
                              patchPerNode(n.name, {
                                repoPrompt: v === 'repo' ? (repoPrompt ?? defaultRepoPrompt) : undefined,
                              })
                            }
                          >
                            <SelectTrigger className="h-7 w-[200px] cursor-pointer" data-testid={`claude-per-node-prompt-source-${n.name}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="system" className="cursor-pointer">Default (system)</SelectItem>
                              <SelectItem value="repo" className="cursor-pointer">From repository</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      {(repoPrompt || n.promptDeferred) && (
                        <RepoPromptPicker
                          projectId={projectId}
                          connectorTypes={connectorTypes}
                          value={repoPrompt ?? defaultRepoPrompt}
                          onChange={rp => patchPerNode(n.name, { repoPrompt: rp })}
                          testIdPrefix={`claude-per-node-${n.name}-repo-prompt`}
                        />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {overrideCount > 0 && (
              <div className="mt-1 flex items-center justify-end border-t border-border px-2 pt-2">
                <button
                  type="button"
                  className="cursor-pointer text-[11px] font-semibold text-info-foreground hover:underline"
                  onClick={handleResetAll}
                >
                  Reset to defaults
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
